/**
 * Email content generator for bulk wine exports.
 * Generates HTML email bodies with B2B professional tone.
 */

import { calcPricePerLitreWithMargin, formatMoneyAmount } from '@ministry-of-wine/shared';

export interface EmailContent {
  subject: string;
  body: string;
}

export interface UserInfo {
  name: string;
  email?: string;
  phone?: string;
}

// Flexible wine interface that works with both list and detail page data
interface WineData {
  code: string;
  variety?: string;
  primaryVarietyName?: string;
  vintage: string | number;
  volumeLitres?: number;
  totalVolumeLiters?: number;
  volume?: number;
  region?: { name: string; country: { name: string } };
  regionName?: string;
  countryName?: string;
  wineCompositions?: Array<{ grape: { name: string }; percentage: number }>;
  composition?: string;
  pricePerLitre?: number;
  pricePerLiter?: number;
  priceMargin?: number | string | null;
  priceMarginUnit?: string | null;
  priceCurrency: string;
  supplier?: { name: string };
  supplierName?: string;
}

// --- Helpers ---

const formatNumber = (value: number, decimals = 2): string =>
  value.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

function getRegionName(wine: WineData): string {
  if (wine.region) return wine.region.name;
  if (wine.regionName) return wine.regionName;
  return '';
}

function getVariety(wine: WineData): string {
  return wine.variety || wine.primaryVarietyName || 'Unknown';
}

function getVolume(wine: WineData): number {
  // Detail-page payloads carry `volume`, list-row payloads carry `volume` too,
  // while richer shapes use volumeLitres/totalVolumeLiters — accept all.
  return wine.volumeLitres || wine.totalVolumeLiters || wine.volume || 0;
}

/**
 * The price shown in the email is what the customer pays — the supplier's price
 * per litre PLUS the Ministry of Wine margin — matching the marked-up price in
 * the attached PDF. Falls back to the base price when no margin applies.
 */
function getPrice(wine: WineData): number {
  const basePrice = wine.pricePerLitre ?? wine.pricePerLiter ?? 0;
  const markedUp = calcPricePerLitreWithMargin(basePrice, wine.priceMargin, wine.priceMarginUnit);
  return markedUp ?? basePrice;
}

/**
 * Format a wine as a compact one-liner.
 * Example: "2025 Marlborough Pinot Noir code MARL-PN-2025 - 13,500 ltrs NZD 4.80/ltr"
 */
function formatWineAsLine(wine: WineData): string {
  const region = getRegionName(wine);
  const variety = getVariety(wine);
  const volume = formatNumber(getVolume(wine), 0);
  // Use the shared money formatter (round-half-up on cents) so the price matches
  // the PDF to the cent — formatNumber/toFixed can disagree on .xx5 values.
  const price = formatMoneyAmount(getPrice(wine), 2) ?? formatNumber(getPrice(wine), 2);

  return `${wine.vintage} ${region} ${variety} code ${wine.code} - ${volume} ltrs ${wine.priceCurrency} ${price}/ltr`;
}

// --- Templates ---

const REQUIREMENTS_LIST = `<ul>
<li>Code</li>
<li>Price per litre and whether or not it needs to be bottle ready</li>
<li>Volume needed</li>
<li>Payment term</li>
<li>When it would be collected</li>
<li>Markets it needs to be eligible for export too</li>
</ul>`;

function buildSignature(user?: UserInfo): string {
  return `<p>Kind regards,</p>
<p>${user?.name || 'Matt Ussher'}<br>
Winemaker<br>
Ministry of Wine Ltd.<br>
ph. ${user?.phone || '+64 (0) 21 103 1136'}<br>
<a href="https://www.ministryofwine.co.nz">www.ministryofwine.co.nz</a></p>`;
}

// --- Main export ---

export function generateBulkWineEmailContent(wines: WineData[], user?: UserInfo): EmailContent {
  if (wines.length === 0) {
    return { subject: 'Bulk Wine Samples', body: '' };
  }

  const signature = buildSignature(user);

  if (wines.length === 1) {
    const wine = wines[0];
    const subject = `${wine.code} - ${getVariety(wine)} ${wine.vintage}`;
    const wineDetail = formatWineAsLine(wine);

    const body = `<p>Hi,</p>
<p>I have sent samples of the following wine (details attached), prices exclude GST:</p>
<p><b>${wineDetail}</b></p>
<p>If this is of interest I'll need the following for an offer:</p>
${REQUIREMENTS_LIST}
${signature}`;

    return { subject, body };
  } else {

      // Multiple wines
      const subject = `Bulk Wine Samples`;
      const wineList = wines.map(formatWineAsLine).join('<br>');

      const body = `<p>Hi,</p>
<p>I have sent samples of the following wines (details attached), prices exclude GST:</p>
<p><b>${wineList}</b></p>
<p>If they are of interest I'll need the following for an offer:</p>
${REQUIREMENTS_LIST}
${signature}`;

      return {subject, body};
  }
}
